import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

function matchPermission(userPerm: string, requiredPerm: string): boolean {
  if (userPerm === '*:*') {
    return true
  }
  if (userPerm === requiredPerm) {
    return true
  }
  const userParts = userPerm.split(':')
  const requiredParts = requiredPerm.split(':')
  if (userParts.length !== requiredParts.length) {
    return false
  }
  for (let i = 0; i < userParts.length; i++) {
    if (userParts[i] !== '*' && userParts[i] !== requiredParts[i]) {
      return false
    }
  }
  return true
}

export function usePermission() {
  const authStore = useAuthStore()

  const hasPermission = (permission: string | string[]): boolean => {
    const permissions = authStore.user?.permissions || []
    
    if (!permission || permission.length === 0) {
      return true
    }

    if (Array.isArray(permission)) {
      return permission.some(p => 
        permissions.some(userPerm => matchPermission(userPerm, p))
      )
    }

    return permissions.some(userPerm => matchPermission(userPerm, permission))
  }

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    const userPermissions = authStore.user?.permissions || []
    return requiredPermissions.every(p => 
      userPermissions.some(userPerm => matchPermission(userPerm, p))
    )
  }

  const hasAnyPermission = (permissions: string[]): boolean => {
    return hasPermission(permissions)
  }

  const can = computed(() => (permission: string | string[]) => hasPermission(permission))
  const canAll = computed(() => (permissions: string[]) => hasAllPermissions(permissions))
  const canAny = computed(() => (permissions: string[]) => hasAnyPermission(permissions))

  return {
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    can,
    canAll,
    canAny
  }
}
